import { __ } from '@wordpress/i18n';
import React, { Fragment, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

// Components.
import Filters from '../filters';
import SafeHTML from '../safe-html';
import Eyebrow from '../eyebrow';
// Services.
import fetchAll from '../../js/services/fetch-all';
import filterResearch from '../../js/services/filters/filter-research';
import filtersFromResearch
  from '../../js/services/filters/filters-from-research';

const ResearchApp = () => {
  // Config.
  const loadMoreAmount = 5;

  // State.
  const [activeFilters, setActiveFilters] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [contentEndIndex, setContentIndex] = useState(loadMoreAmount);
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [programAreas, setProgramAreas] = useState([]);
  const [research, setResearch] = useState([]);
  const [researchFiltered, setResearchFiltered] = useState([]);
  const [topics, setTopics] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);

  /**
   * Given an item's program areas, returns a subset of the program areas
   * array that contains the program areas that are included with the item.
   * @param {Array} itemProgramAreas - The program areas for the item.
   * @returns {Array} A subset of the programAreas array.
   */
  const filterProgramAreas = (itemProgramAreas) => programAreas
    .filter(({ id }) => itemProgramAreas.includes(id));

  /**
   * Formats provided date string into more readable, American English style date.
   *
   * @param {string} datestring - Date string to format.
   */
  const formatDate = (datestring) => {
    const date = new Date(datestring);
    // eslint-disable-next-line max-len
    return date.toLocaleString('default', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  /**
   * Updates the filtered content list and list of available filters in response
   * to data loading or active filters being applied.
   * @param {Array} nextResearch - The new research array to filter.
   * @param {Array} nextCommunities - Array of community taxonomy terms.
   * @param {Array} nextProgramAreas - Array of program area taxonomy terms.
   * @param {Array} nextTopics - Array of topic taxonomy terms.
   * @param {Array} nextReportTypes - Array of report types taxonomy terms.
   * @param {Array} nextActiveFilters - The new active filters array to apply.
   */
  const updateFilters = (
    nextResearch,
    nextCommunities,
    nextProgramAreas,
    nextTopics,
    nextReportTypes,
    nextActiveFilters
  ) => {
    const nextResearchFiltered = filterResearch(
      nextResearch,
      nextActiveFilters
    );
    setResearchFiltered(nextResearchFiltered);
    setFilters(filtersFromResearch(
      nextResearchFiltered,
      nextCommunities,
      nextProgramAreas,
      nextTopics,
      nextReportTypes,
    ));
    setContentIndex(loadMoreAmount);
  };

  // Update results when filters change.
  useEffect(() => updateFilters(
    research,
    communities,
    programAreas,
    topics,
    reportTypes,
    activeFilters,
  ), [activeFilters]);

  // Fetch all posts on mount.
  useEffect(() => {
    (async () => {
      const allPosts = await fetchAll([
        'communities',
        'program-areas',
        'report',
        'topics',
        'report-types',
      ]);

      const {
        communities: communitiesData,
        'program-areas': programAreasData,
        report: reports,
        topics: topicsData,
        'report-types': reportTypesData,
      } = allPosts;

      setCommunities(communitiesData);
      setProgramAreas(programAreasData);
      setResearch(reports);
      setTopics(topicsData);
      setReportTypes(reportTypesData);
      updateFilters(
        reports,
        communitiesData,
        programAreasData,
        topicsData,
        reportTypesData,
        activeFilters
      );
      setLoading(false);
    })();
  }, []);

  return (
    <div className="content-listing">
      <h2>
        {__('Browse Knight Research', 'knight-foundation-app')}
      </h2>
      {loading ? (
        <div className="content-listing__loading">
          {__('Loading...', 'knight-foundation-app')}
        </div>
      ) : (
        <div className="content-listing__panel">
          <div className="content-listing__panel-content">
            <Fragment>
              {researchFiltered.length ? (
                <Fragment>
                  <ul className="content-listing__content-list">
                    {researchFiltered.slice(0, contentEndIndex)
                      .map((item) => {
                        const publicationDate = formatDate(item.date);
                        const imageFileName = item.featured_media_url;
                        const itemProgramAreas
                          = filterProgramAreas(item['program-areas']);
                        return (
                          <li
                            className="content-listing__item"
                            key={item.id}
                          >
                            {imageFileName ? (
                              <div className="item__image large">
                                <img
                                  alt={item.title.rendered}
                                  src={`${imageFileName}?resize=250x250`}
                                />
                              </div>
                            ) : (
                              <div className="item__image large placeholder" />
                            )}
                            <div className="item__details">
                              <a
                                className="item__cover"
                                href={item.link}
                              >
                                {itemProgramAreas.length ? (
                                  <Eyebrow
                                    label={itemProgramAreas[0].name}
                                    color={itemProgramAreas[0].theme ?
                                      itemProgramAreas[0].theme : ''}
                                  />
                                ) : null}
                                <SafeHTML
                                  className="item__title"
                                  html={item.title.rendered}
                                  tag="div"
                                />
                              </a>
                              {item.excerpt && (
                                <SafeHTML
                                  className="item__excerpt"
                                  html={item.excerpt.rendered}
                                  tag="div"
                                />
                              )}
                              <div className="item__lower-details">
                                <span>{`${item.type} · `}</span>
                                {publicationDate && (
                                  <SafeHTML
                                    className="item__date"
                                    html={publicationDate}
                                    tag="div"
                                  />
                                )}
                              </div>

                            </div>
                          </li>
                        );
                      })}
                  </ul>
                  {researchFiltered.length > contentEndIndex &&
                  <div className="content-listing__more">
                    <button
                      onClick={() =>
                        setContentIndex(contentEndIndex + loadMoreAmount)
                      }
                    >
                      {__('Load More', 'knight-foundation-app')}
                    </button>
                  </div>
                  }
                </Fragment>
              ) : (
                <div className="content-listing__noresults">
                  {__(
                    'There aren\'t any reports matching those filters.',
                    'knight-foundation-app'
                  )}
                </div>
              )}
            </Fragment>
          </div>
          <div className="research-listing__filters research__filters">
            <Filters
              activeFilters={activeFilters}
              enableKeywordSearch
              enableResetButton
              enableSeeMoreButton
              filters={filters}
              initialOpen={false}
              onChange={setActiveFilters}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const appElement = document.querySelector('.research-list-v2');
if (appElement) {
  ReactDOM.render(React.createElement(ResearchApp, null), appElement);
}
